import env from "../variables/config";

const apiUrl = env.apiUrl;

function authHeaders() {
    return { Authorization: `Bearer ${localStorage.getItem("token")}` };
}

export interface RowLog {
    rowIndex: number;
    siret: string;
    siren: string;
    name: string;
    city: string;
    nafCode: string;
    nafLabel: string;
    // updated
    updatedFields?: string[];
    signalAdded?: boolean;
    // skipped
    skipReason?: "no_identifier" | "no_changes" | "duplicate_row";
    // created — sibling establishment with same SIREN already in DB
    sibling?: { siret: string; name: string; city: string };
    // error
    error?: string;
}

export interface SireneImportLogs {
    created: RowLog[];
    updated: RowLog[];
    skipped: RowLog[];
    errors: RowLog[];
}

export interface SireneImportProgress {
    created: number;
    updated: number;
    skipped: number;
    errors: number;
    multiSiret: number;
    totalRows: number;
}

export interface SireneImportResult extends SireneImportProgress {
    logs: SireneImportLogs;
}

export type SireneImportEvent =
    | { type: "progress"; data: SireneImportProgress }
    | { type: "done"; data: SireneImportResult }
    | { type: "error"; data: { message: string } };

// ── Closures ─────────────────────────────────────────────────────────────────

export interface ClosureRowLog {
    rowIndex: number;
    siret: string;
    siren: string;
    name: string;
    city: string;
    nafCode: string;
    closingDate?: string;
    openingDate?: string;
    sibling?: { siret: string; name: string; city: string };
    linkedPeople?: number;
    eventCreated?: boolean;
    skipReason?: "no_siret" | "duplicate_row" | "already_closed" | "not_found";
    error?: string;
}

export interface ClosureImportProgress {
    closed: number;
    created: number;
    skipped: number;
    errors: number;
    eventsFrozen: number;
    totalRows: number;
}

export interface ClosureImportResult extends ClosureImportProgress {
    logs: {
        closed: ClosureRowLog[];
        created: ClosureRowLog[];
        skipped: ClosureRowLog[];
        errors: ClosureRowLog[];
    };
}

export type ClosureImportEvent =
    | { type: "progress"; data: ClosureImportProgress }
    | { type: "done"; data: ClosureImportResult }
    | { type: "error"; data: { message: string } };

export async function importEtablissementsClosures(
    file: File,
    createMissing: boolean,
    onEvent: (event: ClosureImportEvent) => void
): Promise<void> {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("createMissing", String(createMissing));

    const response = await fetch(`${apiUrl}/admin/import/etablissements-closures`, {
        method: "POST",
        headers: authHeaders(),
        body: formData,
    });

    await consumeSse(response, onEvent as (e: any) => void);
}

// ── Establishments ───────────────────────────────────────────────────────────

export async function importEtablissementsSirene(
    file: File,
    onEvent: (event: SireneImportEvent) => void
): Promise<void> {
    const formData = new FormData();
    formData.append("file", file);

    const response = await fetch(`${apiUrl}/admin/import/etablissements-sirene`, {
        method: "POST",
        headers: authHeaders(),
        body: formData,
    });

    await consumeSse(response, onEvent as (e: any) => void);
}

/** Shared SSE reader: both imports stream progress/done/error the same way. */
async function consumeSse(
    response: Response,
    onEvent: (event: { type: string; data: any }) => void
): Promise<void> {
    if (!response.ok) {
        const text = await response.text();
        let message = `HTTP ${response.status}`;
        try { message = JSON.parse(text)?.error || message; } catch (_) {}
        throw new Error(message);
    }

    if (!response.body) throw new Error("Response body is not readable");

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";

    while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        const parts = buffer.split("\n\n");
        buffer = parts.pop() ?? "";

        for (const part of parts) {
            if (!part.trim()) continue;
            const lines = part.split("\n");
            let eventType = "message";
            let dataStr = "";
            for (const line of lines) {
                if (line.startsWith("event: ")) eventType = line.slice(7).trim();
                else if (line.startsWith("data: ")) dataStr = line.slice(6);
            }
            if (!dataStr) continue;
            try {
                const data = JSON.parse(dataStr);
                if (eventType === "progress") onEvent({ type: "progress", data });
                else if (eventType === "done") onEvent({ type: "done", data });
                else if (eventType === "error") onEvent({ type: "error", data });
            } catch (_) {}
        }
    }
}
